import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  setCurrentUserOverride,
} from "@/lib/store";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

/**
 * ProfileEdit
 *
 * Editable form for the sister's basic profile: display name, email, city,
 * Instagram handle, and password change. The page is split into two visual
 * sections so changing your password is clearly a different action from
 * editing your name. We never log or expose the current password value.
 */

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <div
        className="mnf-eyebrow"
        style={{ color: "var(--mnf-tg)", marginBottom: 6 }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div
          className="mnf-body italic mt-1.5"
          style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}
        >
          {hint}
        </div>
      )}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 12,
  border: "1px solid rgba(30,74,53,0.18)",
  background: "var(--mnf-surface)",
  fontSize: 15,
  color: "var(--mnf-tx)",
  fontFamily: "var(--mnf-body), sans-serif",
};

export default function ProfileEdit() {
  const me = useStoreQuery(() => queries.currentUser());
  const updateProfile = useStoreMutation(mutations.updateProfile);
  const updateProfileOnServer = useConvexMutation(api.users.updateMyProfile);
  const [, setLocation] = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [instagram, setInstagram] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  // Hydrate form once `me` resolves.
  useEffect(() => {
    if (!me) return;
    setName(me.name);
    setEmail(me.email);
    setCity(me.city ?? "");
    setInstagram(me.instagram ?? "");
  }, [me]);

  if (!me) return null;

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSaved(false);
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setProfileError("Please share your name so coach Kiley can greet you.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setProfileError("That email does not look right. Please check it.");
      return;
    }
    const trimmedCity = city.trim();
    setProfileSaving(true);
    try {
      // Persist name + city to Convex first — that is the record that
      // survives a reload. The local store write below keeps the rest of
      // this session's UI (email, Instagram) in sync without a refetch;
      // it is keyed to her real id now, not the seeded demo account, so it
      // can no longer land on someone else's row.
      await updateProfileOnServer({ name: trimmedName, city: trimmedCity });
      updateProfile(me.id, {
        name: trimmedName,
        email: trimmedEmail,
        city: trimmedCity,
        instagram: instagram.trim(),
      });
      // Reflect the new name immediately (header, avatar initials) instead
      // of making her wait for a reload to see her own edit take effect.
      setCurrentUserOverride({ id: me.id, name: trimmedName, tier: me.tier, role: me.role });
      setProfileSaved(true);
      window.setTimeout(() => setProfileSaved(false), 2400);
    } catch (err) {
      console.error("[profile-edit] could not save profile", err);
      setProfileError(
        "We couldn't save your changes. Check your connection and try again.",
      );
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <ScreenHeader
        variant="cream"
        eyebrow="Account"
        title="Edit my profile"
        subtitle="Your name, email, and how the sisters find you."
        trailing={
          <Link href="/profile" className="no-underline" data-testid="link-back-to-profile">
            <div
              className="inline-flex items-center justify-center hover-elevate"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "rgba(30,74,53,0.08)",
                color: "var(--mnf-f)",
              }}
            >
              <Icon name="arrow_back" size={20} />
            </div>
          </Link>
        }
      />

      {/* Profile form */}
      <form onSubmit={handleSaveProfile} style={{ padding: "0 16px" }}>
        <GlassCard padding={18} data-testid="card-profile-form">
          <div className="flex flex-col gap-4">
            <Field label="Name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                placeholder="Hannah Doyle"
                data-testid="input-name"
              />
            </Field>
            <Field
              label="Email"
              hint="Used for renewal reminders and password changes."
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                placeholder="you@example.com"
                data-testid="input-email"
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                style={inputStyle}
                placeholder="Nashville, TN"
                data-testid="input-city"
              />
            </Field>
            <Field
              label="Instagram"
              hint="Optional. Sisters in your group can find you here."
            >
              <input
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                style={inputStyle}
                placeholder="hannahd.flourish"
                data-testid="input-instagram"
              />
            </Field>

            {profileError && (
              <div
                role="alert"
                data-testid="text-profile-error"
                className="mnf-body"
                style={{
                  fontSize: 13,
                  color: "var(--mnf-coral)",
                  background: "rgba(192,120,74,0.08)",
                  padding: "10px 12px",
                  borderRadius: 10,
                }}
              >
                {profileError}
              </div>
            )}
            {profileSaved && (
              <div
                role="status"
                data-testid="text-profile-saved"
                className="mnf-body"
                style={{
                  fontSize: 13,
                  color: "#2C6248",
                  background: "rgba(168,212,184,0.30)",
                  padding: "10px 12px",
                  borderRadius: 10,
                }}
              >
                Saved. Beautifully done.
              </div>
            )}

            <div className="flex items-center gap-2 mt-1">
              <Btn
                type="submit"
                variant="primary"
                disabled={profileSaving}
                data-testid="button-save-profile"
              >
                {profileSaving ? "Saving…" : "Save changes"}
              </Btn>
              <Btn
                type="button"
                variant="ghost"
                onClick={() => setLocation("/profile")}
                data-testid="button-cancel-profile"
              >
                Cancel
              </Btn>
            </div>
          </div>
        </GlassCard>
      </form>

      {/* Read-only fields: tier, life stage, member since */}
      <div style={{ padding: "16px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          Membership snapshot
        </div>
        <GlassCard padding={16} data-testid="card-membership-snapshot">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                Tier
              </span>
              <PillTag tone="forest">{me.tier}</PillTag>
            </div>
            <div className="flex items-center justify-between">
              <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                Life stage
              </span>
              <span
                className="mnf-body"
                style={{ fontSize: 14, color: "var(--mnf-tx)", fontWeight: 600, textTransform: "capitalize" }}
                data-testid="text-life-stage"
              >
                {me.lifeStage ?? "Not set"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                Bible translation
              </span>
              <span
                className="mnf-body"
                style={{ fontSize: 14, color: "var(--mnf-tx)", fontWeight: 600 }}
                data-testid="text-translation"
              >
                {me.translation}
              </span>
            </div>
            <div
              className="mnf-body italic"
              style={{ fontSize: 12, color: "var(--mnf-tg)", textAlign: "center", marginTop: 4 }}
            >
              To change your tier, head to{" "}
              <Link href="/billing" style={{ color: "var(--mnf-f)", textDecoration: "underline" }}>
                Billing
              </Link>
              .
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Password change.

          REMOVED: a full three-field "Change password" form wired to
          mutations.changePassword (client/src/lib/store.ts). That mutation is
          a demo stub — it accepts ANY non-empty string as the current
          password, writes `demo-hash:<length>` into the in-memory store
          against the hardcoded seed account, and never calls Convex Auth. The
          UI then reported "Password updated." The real password was unchanged
          and the sister had been told otherwise, about her credentials.

          Convex Auth already ships a working email reset (flow "reset" ->
          "reset-verification", see pages/SignIn.tsx and
          convex/ResendOTPPasswordReset.ts). Until that is reachable from
          inside an authenticated session, point at the flow that actually
          works rather than simulating one that does not. */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          Change password
        </div>
        <GlassCard padding={18} data-testid="card-password-reset-info">
          <p
            className="mnf-body"
            style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--mnf-t)" }}
          >
            Passwords are changed by email, so no one can change yours from a
            device you left signed in.
          </p>
          <p
            className="mnf-body italic mt-2"
            style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--mnf-tg)" }}
          >
            Sign out from your profile, then choose &ldquo;Forgot password&rdquo; on the
            sign-in screen. We&rsquo;ll send a code to{" "}
            {me?.email ? <strong>{me.email}</strong> : "your email"}.
          </p>
          <div className="mt-4">
            <Link href="/profile" className="no-underline">
              <Btn variant="secondary" data-testid="button-go-sign-out">
                Go to profile to sign out
              </Btn>
            </Link>
          </div>
        </GlassCard>
      </div>

      {/* Delete account (placeholder) */}
      <div style={{ padding: "20px 16px 0" }}>
        <div
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-tg)", marginBottom: 10, paddingLeft: 4 }}
        >
          Danger zone
        </div>
        <GlassCard padding={16} data-testid="card-delete-account">
          <div className="flex items-start gap-3">
            <Icon name="warning" size={22} style={{ color: "var(--mnf-coral)", marginTop: 2 }} />
            <div className="flex-1">
              <div
                className="mnf-body"
                style={{ fontSize: 14, color: "var(--mnf-tx)", fontWeight: 600 }}
              >
                Delete account
              </div>
              <div
                className="mnf-body"
                style={{ fontSize: 12.5, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.5 }}
              >
                Permanently removes your meals, journal, and prayer entries. This cannot be undone. Email{" "}
                <a
                  href="mailto:movenourishflourishsupport@gmail.com?subject=Delete%20my%20account"
                  style={{ color: "var(--mnf-f)" }}
                >
                  support
                </a>{" "}
                to start.
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
